// Scoped, host-mediated data access for widgets (the `momentum:fetch` bridge call).
//
// A widget never holds a token. It asks the host for data by scope + path; the host
// checks that the widget's manifest grants the scope, that the path/method are on that
// scope's allowlist, applies a per-widget rate limit, and only then calls the Momentum
// AMP API with the signed-in user's own credentials. The user's permissions still
// apply server-side — scopes narrow what a widget may ask for, they never widen it.

import type { WidgetManifest } from '../../services/myToolsApi';

const API_BASE = process.env.REACT_APP_Auth_Url || '';

/** Every backend controller a widget scope can be routed to. AMPNowcerts is the one AMS proxy
 *  (needs the NCToken header); everything else is a plain Bearer-authenticated Momentum AMP
 *  controller, same as the app's own pages use directly. */
const API_BASES = {
  ampnowcerts: `${API_BASE}/api/AMPNowcerts/`,
  momentumkanban: `${API_BASE}/api/MomentumKanban/`,
  opportunitiessubmission: `${API_BASE}/api/OpportunitiesSubmission/`,
  userpreferences: `${API_BASE}/api/UserPreferences/`,
  webhookanalytics: `${API_BASE}/api/WebhookAnalytics/`,
  companysettings: `${API_BASE}/api/CompanySettings/`,
  momentumroutine: `${API_BASE}/api/MomentumRoutine/`,
  marketplace: `${API_BASE}/api/Marketplace/`,
  momentumamp: `${API_BASE}/api/MomentumAMP/`,
  ampmanagement: `${API_BASE}/api/AMPManagement/`,
  carrierdownload: `${API_BASE}/api/carrier-download/`,
  linqura: `${API_BASE}/api/Linqura/`,
} as const;
type ApiBaseKey = keyof typeof API_BASES;

export const API_PERMISSION_PREFIX = 'api:';
export const FETCH_RATE_LIMIT = { max: 60, windowMs: 60_000 };
const MAX_BODY_BYTES = 256 * 1024;

type Method = 'GET' | 'POST' | 'DELETE' | 'PATCH' | 'PUT';
interface ScopeRoute {
  method: Method;
  pattern: RegExp;
  /** The route returns file bytes. They're handed to the widget base64-encoded ({ contentType,
   *  size, base64 }) — reading them as text, like every other response, corrupted PDFs/images. */
  binary?: boolean;
}
export interface ScopeDefinition {
  label: string;
  description: string;
  /** Which controller this scope's routes live under — see API_BASES. */
  base: ApiBaseKey;
  /** True only for the AMS proxy: attaches the NCToken header and fails closed with 412 when
   *  no AMS account is connected. Every other base is a plain Bearer-authenticated call, same
   *  as the rest of the app already makes — no AMS connection required. */
  needsAmsToken: boolean;
  /** Overrides the default 20s request timeout — only for scopes with a route that's known to
   *  take longer server-side (e.g. a synchronous poll-until-complete call to a third-party API). */
  timeoutMs?: number;
  routes: ScopeRoute[];
  /** Human-readable "METHOD path" strings for the routes above, e.g. "GET renewals" or
   *  "GET insured/{id}/drivers" — kept next to `routes` on purpose so whoever edits the actual
   *  allowlist edits these in the same place, instead of a route list living only in a separate
   *  markdown file that quietly goes stale. This is what widgetSdkLinks' developer guide renders,
   *  so what an author sees here is the same list the host actually enforces against, not a copy
   *  of it. Every entry here must match one of the patterns in `routes` — there's no runtime link
   *  between the two, so a route change must update both.
   */
  examples: string[];
}

const GET = (pattern: RegExp): ScopeRoute => ({ method: 'GET', pattern });
const POST = (pattern: RegExp): ScopeRoute => ({ method: 'POST', pattern });
const DEL = (pattern: RegExp): ScopeRoute => ({ method: 'DELETE', pattern });
const PATCH = (pattern: RegExp): ScopeRoute => ({ method: 'PATCH', pattern });
const PUT = (pattern: RegExp): ScopeRoute => ({ method: 'PUT', pattern });
const SEG = '[A-Za-z0-9_-]+';
// Every scope below that omits `base`/`needsAmsToken` defaults to the AMS proxy — most scopes
// are AMPNowcerts routes, so this keeps those entries as terse as they were before the other
// controllers were added.
const ams = { base: 'ampnowcerts' as const, needsAmsToken: true };

/** Everything a widget can reach, per scope. Paths are relative to the scope's `base` (see
 *  API_BASES) — most scopes are under /api/AMPNowcerts/, a few reach other controllers. */
export const WIDGET_API_SCOPES: Record<string, ScopeDefinition> = {
  'insureds.read': {
    ...ams,
    label: 'Read insureds & prospects',
    description: 'Insured/prospect profiles, drivers, vehicles, boats and lines of business.',
    routes: [
      GET(/^insuredandprospectListById$/), GET(/^prospects$/), GET(/^lines-of-business$/),
      GET(new RegExp(`^insured/${SEG}/(drivers|vehicles|boats)$`)),
    ],
    examples: [
      'GET insuredandprospectListById', 'GET prospects', 'GET lines-of-business',
      'GET insured/{id}/drivers', 'GET insured/{id}/vehicles', 'GET insured/{id}/boats',
    ],
  },
  'insureds.write': {
    ...ams,
    label: 'Create prospects',
    description: 'Create a new prospect.',
    routes: [POST(/^create-prospect$/)],
    examples: ['POST create-prospect'],
  },
  'policies.read': {
    ...ams,
    label: 'Read policies',
    description: 'Policies, policy details and upcoming renewals.',
    routes: [
      GET(/^insuredpolicies$/), GET(/^insuredpoliciesbyid$/), GET(/^policydetails$/),
      GET(/^renewals$/), GET(new RegExp(`^insured/${SEG}/policies$`)),
    ],
    examples: [
      'GET insuredpolicies', 'GET insuredpoliciesbyid', 'GET policydetails',
      'GET renewals', 'GET insured/{id}/policies',
    ],
  },
  'documents.read': {
    ...ams,
    label: 'Read policy documents',
    description: 'List a policy\'s attached files and fetch the file contents.',
    routes: [GET(/^policy-files$/), { ...GET(/^policy-file-proxy$/), binary: true }],
    examples: ['GET policy-files', 'GET policy-file-proxy'],
  },
  'certificates.read': {
    ...ams,
    label: 'Read certificate holder history',
    description: 'Historical certificate holders issued for an insured.',
    routes: [GET(/^historicalcertificateholderlist$/)],
    examples: ['GET historicalcertificateholderlist'],
  },
  'carriers.read': {
    ...ams,
    label: 'Read carrier info',
    description: "A carrier's principal contacts.",
    routes: [GET(new RegExp(`^carrier/${SEG}/principals$`))],
    examples: ['GET carrier/{carrierId}/principals'],
  },
  'opportunities.read': {
    ...ams,
    label: 'Read opportunities',
    description: 'Opportunities, their quotes/policies and pipeline stages.',
    routes: [
      GET(/^opportunities$/), GET(/^opportunities\/quotes-policies$/), GET(/^opportunity-stages$/),
      GET(new RegExp(`^insured/${SEG}/opportunities$`)),
    ],
    examples: [
      'GET opportunities', 'GET opportunities/quotes-policies', 'GET opportunity-stages',
      'GET insured/{id}/opportunities',
    ],
  },
  'opportunities.write': {
    ...ams,
    label: 'Create opportunities & quotes',
    description: 'Create a new opportunity or quote.',
    routes: [POST(/^create-opportunity$/), POST(/^create-quote$/)],
    examples: ['POST create-opportunity', 'POST create-quote'],
  },
  'tasks.read': { ...ams, label: 'Read tasks', description: 'AMS tasks.', routes: [GET(/^tasks$/)], examples: ['GET tasks'] },
  'notes.read': {
    ...ams,
    label: 'Read notes',
    description: 'Notes on insureds and opportunities.',
    routes: [GET(new RegExp(`^insured/${SEG}/notes$`)), GET(new RegExp(`^opportunity/${SEG}/notes$`))],
    examples: ['GET insured/{id}/notes', 'GET opportunity/{id}/notes'],
  },
  'notes.write': {
    ...ams,
    label: 'Add notes',
    description: 'Create notes on insureds and opportunities.',
    routes: [POST(new RegExp(`^insured/${SEG}/notes$`)), POST(new RegExp(`^opportunity/${SEG}/notes$`))],
    examples: ['POST insured/{id}/notes', 'POST opportunity/{id}/notes'],
  },
  'tasks.write': { ...ams, label: 'Create tasks', description: 'Create AMS tasks.', routes: [POST(/^create-task$/)], examples: ['POST create-task'] },
  'sms.write': {
    ...ams,
    label: 'Send text messages',
    description: "Send an SMS text message from the agency's number.",
    routes: [POST(/^send-sms$/)],
    examples: ['POST send-sms'],
  },
  'quoting-runs.read': {
    ...ams,
    label: 'Read rate-quoting run history',
    description: 'History of automated rate-quoting runs for a spreadsheet row.',
    routes: [GET(new RegExp(`^runs/by-row/${SEG}$`))],
    examples: ['GET runs/by-row/{rowId}'],
  },

  // ── Beyond the AMS proxy: plain Bearer-authenticated Momentum AMP controllers ───────────
  'submissions.read': {
    base: 'opportunitiessubmission', needsAmsToken: false,
    label: 'Read submission tracking',
    description: 'Submission Center tracking steps, status history, communication log and AI insights for an opportunity or quote.',
    routes: [
      GET(/^get$/), GET(/^get-by-name$/), POST(/^status-history$/), GET(/^get-communication-history$/),
      GET(/^opportunity-insights$/), GET(/^quote-insights$/),
    ],
    examples: [
      'GET get', 'GET get-by-name', 'POST status-history', 'GET get-communication-history',
      'GET opportunity-insights', 'GET quote-insights',
    ],
  },
  'submissions.write': {
    base: 'opportunitiessubmission', needsAmsToken: false,
    label: 'Update submission tracking',
    description: 'Log a tracking step or communication, update tracking status, or delete a tracking entry.',
    routes: [POST(/^insert$/), DEL(new RegExp(`^delete/${SEG}$`)), POST(/^check-uncheck$/), POST(/^insert-communication-history$/)],
    examples: ['POST insert', 'DELETE delete/{id}', 'POST check-uncheck', 'POST insert-communication-history'],
  },
  'kanban.read': {
    base: 'momentumkanban', needsAmsToken: false,
    label: 'Read kanban tasks',
    description: "The signed-in person's kanban/task board: tasks, stats, categories and configuration.",
    routes: [
      GET(/^tasks$/), GET(/^tasks\/stats$/), GET(new RegExp(`^task/${SEG}$`)), GET(/^tasks\/poll$/),
      GET(/^task\/categories$/), GET(/^task\/category\/configurations$/), GET(/^task\/category\/sop-configs$/),
      GET(/^task\/category\/custom\/list$/), GET(/^task\/command-center\/configuration$/),
      GET(/^agent\/tasks$/), GET(/^agent\/stats$/),
    ],
    examples: [
      'GET tasks', 'GET tasks/stats', 'GET task/{taskId}', 'GET tasks/poll',
      'GET task/categories', 'GET task/category/configurations', 'GET task/category/sop-configs',
      'GET task/category/custom/list', 'GET task/command-center/configuration',
      'GET agent/tasks', 'GET agent/stats',
    ],
  },
  'kanban.write': {
    base: 'momentumkanban', needsAmsToken: false,
    label: 'Create & update kanban tasks',
    description: 'Create, update or archive a kanban task, react to an existing AI comment, or manage board categories/configuration.',
    routes: [
      POST(/^task\/upsert$/), POST(/^task\/upsert\/v2$/), POST(/^task\/archive$/),
      // Not DELETE task/{id}: that action binds a GUID while task ids are numbers, so it can
      // never succeed — archive a task instead (POST task/archive).
      POST(/^task\/comment$/),
      POST(/^task\/category\/configuration\/upsert$/), POST(/^task\/category\/sop-config\/upsert$/),
      POST(/^task\/category\/custom\/upsert$/), DEL(new RegExp(`^task/category/custom/${SEG}$`)),
      POST(/^task\/command-center\/configuration\/upsert$/),
    ],
    examples: [
      'POST task/upsert', 'POST task/upsert/v2', 'POST task/archive',
      'POST task/comment', 'POST task/category/configuration/upsert', 'POST task/category/sop-config/upsert',
      'POST task/category/custom/upsert', 'DELETE task/category/custom/{id}',
      'POST task/command-center/configuration/upsert',
    ],
  },
  'preferences.read': {
    base: 'userpreferences', needsAmsToken: false,
    label: 'Read personal preferences',
    description: "The signed-in person's default landing page and hidden navigation items.",
    routes: [GET(/^defaultpage$/), GET(/^hiddenmenuitems$/)],
    examples: ['GET defaultpage', 'GET hiddenmenuitems'],
  },
  'preferences.write': {
    base: 'userpreferences', needsAmsToken: false,
    label: 'Change personal preferences',
    description: "Change the signed-in person's default landing page or hidden navigation items.",
    routes: [POST(/^defaultpage$/), DEL(/^defaultpage$/), POST(/^hiddenmenuitems$/)],
    examples: ['POST defaultpage', 'DELETE defaultpage', 'POST hiddenmenuitems'],
  },
  'analytics.read': {
    base: 'webhookanalytics', needsAmsToken: false,
    label: 'Read webhook analytics',
    description: "A summary of the signed-in person's webhook activity (commands, routines, recent events).",
    routes: [GET(/^summary$/)],
    examples: ['GET summary'],
  },
  'company-settings.read': {
    base: 'companysettings', needsAmsToken: false,
    label: 'Read company settings',
    description: "The company's Digital Workforce harness type setting.",
    routes: [GET(/^harness-type$/)],
    examples: ['GET harness-type'],
  },
  'routines.read': {
    base: 'momentumroutine', needsAmsToken: false,
    label: 'Read routines',
    description: "The signed-in person's automated routines, run history and stats.",
    routes: [
      POST(/^list$/), GET(new RegExp(`^${SEG}$`)), GET(/^templates$/), GET(/^runs\/recent$/),
      GET(/^stats$/), GET(new RegExp(`^${SEG}/history$`)), GET(new RegExp(`^history/${SEG}/executions$`)),
    ],
    examples: [
      'POST list', 'GET {id}', 'GET templates', 'GET runs/recent', 'GET stats',
      'GET {id}/history', 'GET history/{runId}/executions',
    ],
  },
  'routines.write': {
    base: 'momentumroutine', needsAmsToken: false,
    label: 'Create & update routines',
    description: 'Create, update, pause/resume or delete a routine, or react to a run.',
    routes: [
      POST(/^upsert$/), DEL(new RegExp(`^${SEG}$`)), PATCH(new RegExp(`^${SEG}/toggle$`)),
      PATCH(new RegExp(`^history/${SEG}/feedback$`)),
    ],
    examples: ['POST upsert', 'DELETE {id}', 'PATCH {id}/toggle', 'PATCH history/{runId}/feedback'],
  },
  'marketplace.read': {
    base: 'marketplace', needsAmsToken: false,
    label: 'Browse the Toolbox catalog',
    description: 'The Marketplace module catalog: browse modules, read a module\'s detail, and its reviews. Public catalog data, not agency-specific.',
    routes: [GET(/^modules$/), GET(new RegExp(`^modules/${SEG}$`)), GET(new RegExp(`^reviews/${SEG}$`))],
    examples: ['GET modules', 'GET modules/{moduleId}', 'GET reviews/{moduleId}'],
  },
  'marketplace.write': {
    base: 'marketplace', needsAmsToken: false,
    label: 'Manage installed tools & reviews',
    description: "Install, enable/disable, or uninstall a module for the caller's agency, and submit a rating/review. The review is posted under the signed-in person's own name — a widget cannot supply a different one.",
    routes: [POST(/^install$/), DEL(new RegExp(`^uninstall/${SEG}$`)), PUT(/^toggle$/), POST(/^reviews$/)],
    examples: ['POST install', 'DELETE uninstall/{moduleId}', 'PUT toggle', 'POST reviews'],
  },
  'myamp.read': {
    base: 'momentumamp', needsAmsToken: false,
    label: 'Read MyAMP history & files',
    description: "The signed-in person's MyAMP AI-assistant conversation history, uploaded files, and tool-usage stats. Some responses include a short-lived file-download link — once returned, that link works outside this bridge, so treat it the same as any other data the widget is granted.",
    routes: [
      GET(new RegExp(`^momentum-my-amp-history/${SEG}$`)), GET(/^file$/),
      GET(new RegExp(`^momentum-my-amp-chat-history/${SEG}$`)), POST(/^momentum-my-amp-recent-history$/),
      GET(/^insurance-agency-files$/), GET(/^channel-files$/), GET(/^insurance-agency-file$/), GET(/^tools$/),
    ],
    examples: [
      'GET momentum-my-amp-history/{conversationId}', 'GET file', 'GET momentum-my-amp-chat-history/{chatId}',
      'POST momentum-my-amp-recent-history', 'GET insurance-agency-files', 'GET channel-files',
      'GET insurance-agency-file', 'GET tools',
    ],
  },
  'myamp.write': {
    base: 'momentumamp', needsAmsToken: false,
    label: 'React to MyAMP chat turns',
    description: "Submit like/dislike feedback on one of the signed-in person's own MyAMP chat turns.",
    routes: [POST(/^feedback$/)],
    examples: ['POST feedback'],
  },
  'ampmanagement.read': {
    base: 'ampmanagement', needsAmsToken: false,
    label: 'Read Custom AMPs',
    description: "The agency's saved Custom AMP chatbots and the premade catalog.",
    routes: [GET(/^list$/), GET(/^premade$/)],
    examples: ['GET list', 'GET premade'],
  },
  'ampmanagement.write': {
    base: 'ampmanagement', needsAmsToken: false,
    label: 'Create & delete Custom AMPs',
    description: "Create or update the agency's Custom AMP chatbots, or delete one.",
    routes: [POST(/^upsert$/), DEL(new RegExp(`^delete/${SEG}$`))],
    examples: ['POST upsert', 'DELETE delete/{id}'],
  },
  'carrier-download.read': {
    base: 'carrierdownload', needsAmsToken: false,
    label: 'Read carrier download requests',
    description: "The agency's bulk carrier-download requests and their status.",
    routes: [GET(/^my-requests$/)],
    examples: ['GET my-requests'],
  },
  'carrier-download.write': {
    base: 'carrierdownload', needsAmsToken: false,
    label: 'Submit a carrier download request',
    description: "Submit a new bulk carrier-download request for the agency. Refused with 409 while one is already pending.",
    routes: [POST(/^request$/)],
    examples: ['POST request'],
  },
  'linqura.read': {
    base: 'linqura', needsAmsToken: false,
    label: 'Read business-enrichment logs',
    description: 'The logged API calls for one Linqura business-enrichment run.',
    routes: [GET(new RegExp(`^logs/${SEG}$`))],
    examples: ['GET logs/{runId}'],
  },
  'linqura.write': {
    base: 'linqura', needsAmsToken: false,
    // Orchestrates an OAuth token fetch, an order submission and a poll-until-complete loop
    // server-side — routinely takes 10-85s to resolve, well past every other scope's call.
    timeoutMs: 90_000,
    label: 'Run business enrichment',
    description: 'Look up firmographic/OSHA/FMCSA/risk data for a business name. Can take up to about 90 seconds to resolve — this is a real third-party lookup, not a quick call.',
    routes: [POST(/^enrich$/)],
    examples: ['POST enrich'],
  },
};

export function widgetApiScopes(manifest: WidgetManifest | null | undefined): string[] {
  return (manifest?.permissions ?? [])
    .filter(p => p.toLowerCase().startsWith(API_PERMISSION_PREFIX))
    .map(p => p.slice(API_PERMISSION_PREFIX.length).toLowerCase());
}

export interface WidgetFetchRequest {
  id: string;
  scope: string;
  path: string;
  method?: string;
  query?: Record<string, unknown> | null;
  body?: unknown;
}

export interface WidgetFetchResult {
  id: string;
  ok: boolean;
  status?: number;
  data?: unknown;
  error?: string;
}

/** Sliding-window limiter, one per mounted widget frame. */
export class RateLimiter {
  private hits: number[] = [];
  constructor(private max = FETCH_RATE_LIMIT.max, private windowMs = FETCH_RATE_LIMIT.windowMs) {}
  take(): boolean {
    const now = Date.now();
    this.hits = this.hits.filter(t => now - t < this.windowMs);
    if (this.hits.length >= this.max) return false;
    this.hits.push(now);
    return true;
  }
}

function normalizePath(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const path = raw.trim().replace(/^\/+/, '');
  if (!path || path.length > 300) return null;
  if (path.includes('..') || path.includes('?') || path.includes('#') || path.includes('//') || /\s/.test(path)) return null;
  return path;
}

function buildQuery(query: unknown): string {
  if (!query || typeof query !== 'object' || Array.isArray(query)) return '';
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query as Record<string, unknown>)) {
    if (v == null) continue;
    if (Array.isArray(v)) v.forEach(item => params.append(k, String(item)));
    else params.append(k, String(v));
  }
  const s = params.toString();
  return s ? `?${s}` : '';
}

interface RawResponse { status: number; body: string; bytes?: ArrayBuffer; contentType?: string | null }

/** Largest file a binary route hands to a widget (it travels base64-encoded via postMessage). */
const MAX_BINARY_BYTES = 15 * 1024 * 1024;

function toBase64(bytes: ArrayBuffer): string {
  const view = new Uint8Array(bytes);
  let binary = '';
  for (let i = 0; i < view.length; i += 0x8000) binary += String.fromCharCode(...Array.from(view.subarray(i, i + 0x8000)));
  return btoa(binary);
}

// Deliberately XHR, not fetch: the app wraps window.fetch (utils/authInterceptors.ts) and
// treats ANY 401 on an /api/ call as an expired session, clearing storage and redirecting
// to /login. AMS proxy endpoints answer 401 for endpoint-specific reasons (e.g. no NowCerts
// token), and a widget's data request must never be able to log the user out.
function rawRequest(url: string, method: Method, headers: Record<string, string>, body?: string, signal?: AbortSignal, timeoutMs = 20_000, binary = false): Promise<RawResponse> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, url, true);
    if (binary) xhr.responseType = 'arraybuffer';
    Object.entries(headers).forEach(([k, v]) => xhr.setRequestHeader(k, v));
    xhr.onload = () => {
      const contentType = xhr.getResponseHeader('Content-Type');
      if (!binary) { resolve({ status: xhr.status, body: xhr.responseText, contentType }); return; }
      const bytes = xhr.response as ArrayBuffer;
      // Errors still come back as text/JSON; decode them so the message reaches the widget.
      const ok = xhr.status >= 200 && xhr.status < 300;
      resolve({ status: xhr.status, body: ok ? '' : new TextDecoder().decode(bytes), bytes: ok ? bytes : undefined, contentType });
    };
    xhr.onerror = () => reject(new Error('Network error'));
    xhr.ontimeout = () => reject(new Error('Request timed out'));
    xhr.timeout = timeoutMs;
    if (signal) signal.addEventListener('abort', () => xhr.abort(), { once: true });
    xhr.send(body);
  });
}

function parseBody(body: string): unknown {
  if (!body) return null;
  try { return JSON.parse(body); } catch { return body; }
}

// ── Authenticated third-party connections (the `momentum:connect` bridge call) ────────────
//
// Different trust model from the scoped fetch above: there the host already holds the
// credential (the signed-in user's own tokens) and just applies an allowlist. Here the
// credential belongs to a third-party service the widget's *author* wants to call, so it
// has to be supplied by whoever installs the widget and stored somewhere the widget's own
// script can never read. The host therefore never touches that secret either — it calls
// Momentum AMP's own API (authenticated as the signed-in user, same as any other call this
// app makes), and the API itself looks up the secret and attaches it when it forwards the
// request to the connection's declared base URL. See WidgetConnectionProxyService.
export const CONNECT_RATE_LIMIT = { max: 30, windowMs: 60_000 };

export interface WidgetConnectRequest {
  id: string;
  connection: string;
  path?: string;
  method?: string;
  query?: Record<string, string> | null;
  body?: unknown;
}

export interface WidgetConnectResult {
  id: string;
  ok: boolean;
  status?: number;
  data?: unknown;
  contentType?: string | null;
  error?: string;
}

function parseConnectData(raw: unknown, contentType?: string | null): unknown {
  if (typeof raw !== 'string') return raw;
  if (contentType && contentType.toLowerCase().includes('json')) {
    try { return JSON.parse(raw); } catch { /* fall through */ }
  }
  return raw;
}

/** Validates and performs one widget connection request. Never throws — errors come back in the result. */
export async function performConnectionCall(
  toolId: string,
  installationId: string | undefined,
  manifest: WidgetManifest | null | undefined,
  request: WidgetConnectRequest,
  limiter: RateLimiter,
  signal?: AbortSignal,
): Promise<WidgetConnectResult> {
  const id = String(request?.id ?? '');
  const fail = (error: string, status?: number): WidgetConnectResult => ({ id, ok: false, status, error });

  if (!installationId) return fail('Install this widget before it can connect to an outside service.', 412);

  const key = typeof request?.connection === 'string' ? request.connection : '';
  const declared = (manifest?.connections ?? []).find(c => c.key === key);
  if (!declared) return fail(`This widget didn't declare a connection named '${request?.connection}'`, 400);

  let payload: string;
  try {
    payload = JSON.stringify({
      installationId: Number(installationId),
      connection: key,
      path: typeof request.path === 'string' ? request.path : '',
      method: typeof request.method === 'string' ? request.method.toUpperCase() : 'GET',
      query: request.query && typeof request.query === 'object' ? request.query : undefined,
      body: request.body,
    });
  } catch {
    // A BigInt or cyclic body survives postMessage but not JSON.stringify.
    return fail('Request body must be JSON-serialisable', 400);
  }

  if (!limiter.take()) return fail(`Rate limit exceeded (${CONNECT_RATE_LIMIT.max} requests/minute)`, 429);

  const authToken = localStorage.getItem('authToken');
  if (!authToken) return fail('You are signed out.', 401);

  try {
    const response = await rawRequest(
      `${API_BASE}/api/MyTools/${toolId}/widget/connect`, 'POST',
      { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json', Accept: 'application/json' },
      payload, signal,
    );
    const body = parseBody(response.body) as { success?: boolean; message?: string; data?: { ok?: boolean; status?: number; contentType?: string | null; data?: unknown; error?: string } };
    if (response.status < 200 || response.status >= 300 || body?.success === false) {
      return fail(body?.message || `Request rejected (${response.status})`, response.status);
    }
    const inner = body?.data ?? {};
    return { id, ok: !!inner.ok, status: inner.status, contentType: inner.contentType, error: inner.error, data: parseConnectData(inner.data, inner.contentType) };
  } catch (err) {
    return fail(err instanceof Error ? err.message : 'Request failed');
  }
}

/** Validates and performs one widget data request. Never throws — errors come back in the result. */
export async function performScopedFetch(
  manifest: WidgetManifest | null | undefined,
  request: WidgetFetchRequest,
  limiter: RateLimiter,
  signal?: AbortSignal,
): Promise<WidgetFetchResult> {
  const id = String(request?.id ?? '');
  const fail = (error: string, status?: number): WidgetFetchResult => ({ id, ok: false, status, error });

  const scope = typeof request?.scope === 'string' ? request.scope.toLowerCase().replace(/^api:/, '') : '';
  const definition = WIDGET_API_SCOPES[scope];
  if (!definition) return fail(`Unknown API scope '${request?.scope}'`);
  if (!widgetApiScopes(manifest).includes(scope)) return fail(`This widget was not granted 'api:${scope}' — previews run without permissions, and an installer can turn it off under Installed → Configuration.`, 403);

  const method = (typeof request.method === 'string' ? request.method : 'GET').toUpperCase() as Method;
  if (method !== 'GET' && method !== 'POST' && method !== 'DELETE' && method !== 'PATCH' && method !== 'PUT') return fail(`Method ${method} is not supported`);

  const path = normalizePath(request.path);
  if (!path) return fail('Invalid path');
  const route = definition.routes.find(r => r.method === method && r.pattern.test(path));
  if (!route) {
    return fail(`'${method} ${path}' is not allowed under 'api:${scope}'`, 403);
  }
  let body: string | undefined;
  if ((method === 'POST' || method === 'PATCH' || method === 'PUT') && request.body !== undefined && request.body !== null) {
    // postMessage's structured clone happily carries a BigInt or a cyclic object, which
    // JSON.stringify throws on — answer the widget instead of failing with no reply.
    try {
      body = typeof request.body === 'string' ? request.body : JSON.stringify(request.body);
    } catch {
      return fail('Request body must be JSON-serialisable', 400);
    }
    // The cap is in bytes on the wire, not UTF-16 characters.
    if (new TextEncoder().encode(body).length > MAX_BODY_BYTES) return fail('Request body too large', 413);
  }
  // Only a request that is actually going out uses up the widget's budget.
  if (!limiter.take()) return fail(`Rate limit exceeded (${FETCH_RATE_LIMIT.max} requests/minute)`, 429);

  const authToken = localStorage.getItem('authToken');
  if (!authToken) return fail('You are signed out.', 401);

  const headers: Record<string, string> = {
    Authorization: `Bearer ${authToken}`,
    Accept: 'application/json',
  };
  if (definition.needsAmsToken) {
    // The AMS proxy reads the NowCerts token from the NCToken header (same convention as
    // the app's own pages). Without a connected AMS there is nothing to fetch — say so
    // instead of letting the endpoint answer 401.
    const amsToken = localStorage.getItem('amsToken');
    if (!amsToken) return fail('This data needs a connected AMS (NowCerts) account for the signed-in user.', 412);
    headers.NCToken = amsToken;
  }
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  try {
    const response = await rawRequest(`${API_BASES[definition.base]}${path}${buildQuery(request.query)}`, method, headers, body, signal, definition.timeoutMs, route.binary);
    if (route.binary && response.bytes) {
      if (response.bytes.byteLength > MAX_BINARY_BYTES) return fail(`The file is larger than ${MAX_BINARY_BYTES / 1024 / 1024} MB`, 413);
      return { id, ok: true, status: response.status, data: { contentType: response.contentType ?? 'application/octet-stream', size: response.bytes.byteLength, base64: toBase64(response.bytes) } };
    }
    const data = parseBody(response.body);
    if (response.status >= 200 && response.status < 300) return { id, ok: true, status: response.status, data };

    // Controllers word their errors differently: { message }, { errorMessage }, { error } (e.g.
    // carrier-download's 409) or ASP.NET's validation { title }. Pass on whichever is there.
    const errorBody = data as { message?: unknown; errorMessage?: unknown; error?: unknown; title?: unknown };
    const serverMessage = data && typeof data === 'object'
      ? String([errorBody.message, errorBody.errorMessage, errorBody.error, errorBody.title].find(v => typeof v === 'string' && v) ?? '')
      : (typeof data === 'string' ? data.slice(0, 200) : '');
    const declinedBy = definition.needsAmsToken ? 'The AMS declined this request for your account' : 'Momentum AMP declined this request';
    const reason = response.status === 401 || response.status === 403
      ? declinedBy
      : response.status === 404 ? 'Not found'
        : response.status >= 500 ? 'The service failed'
          : 'Request rejected';
    return fail(serverMessage ? `${reason}: ${serverMessage}` : `${reason} (${response.status})`, response.status);
  } catch (err) {
    return fail(err instanceof Error ? err.message : 'Request failed');
  }
}
